// Graph API service for SharePoint data connections
// Uses sharing URL encoding to resolve drive items without needing site/drive IDs

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

const SHARING_URLS = {
  dataAudit: "u!aHR0cHM6Ly90cmlwbGVjYy5zaGFyZXBvaW50LmNvbS86eDovcy9NYWluU2l0ZS9JUUFRdkoyX01sQ3VRNFpIaTR2cUxvU21BYlZvRE42clNxd0NWZW9jeC1XbHY3aw",
  onboarding: "u!aHR0cHM6Ly90cmlwbGVjYy5zaGFyZXBvaW50LmNvbS86eDovcy90Y29hc2lzL0lRQmZxWVI4Tm9wVFJKZ000T05od3RrTUFUUFl2VzYxQXhoLWhiZ1pmN3d6WHFZ",
};

// Fetch with auth header
async function graphFetch(url, accessToken) {
  const res = await fetch(url, {
    headers: { Authorization: "Bearer " + accessToken },
  });
  if (!res.ok) {
    const err = await res.json().catch(function() { return {}; });
    throw new Error("Graph API error " + res.status + ": " + (err.error?.message || res.statusText));
  }
  return res.json();
}

// Resolve a sharing URL to a drive item
async function resolveSharingUrl(encodedUrl, accessToken) {
  const data = await graphFetch(GRAPH_BASE + "/shares/" + encodedUrl + "/driveItem", accessToken);
  return data;
}

// Get all worksheets in a workbook
async function getWorksheets(driveItem, accessToken) {
  const url = GRAPH_BASE + "/drives/" + driveItem.parentReference.driveId +
    "/items/" + driveItem.id + "/workbook/worksheets";
  const data = await graphFetch(url, accessToken);
  return data.value;
}

// Get used range of a worksheet
async function getWorksheetRange(driveItem, sheetName, accessToken) {
  const encodedSheet = encodeURIComponent(sheetName);
  const url = GRAPH_BASE + "/drives/" + driveItem.parentReference.driveId +
    "/items/" + driveItem.id + "/workbook/worksheets/" + encodedSheet + "/usedRange";
  const data = await graphFetch(url, accessToken);
  return data;
}

// ─── DATA AUDIT ───────────────────────────────────────────────────────────────

// Parse the Data Source Master Tracker Sheet1 into structured rows
function parseDataAuditSheet(values) {
  if (!values || values.length < 2) return { rows: [], parseError: "No data found" };

  // Find the header row - look for "Level of Trust" or "Future Owner"
  let headerIdx = -1;
  for (let i = 0; i < Math.min(values.length, 10); i++) {
    const row = values[i].map(function(c) { return String(c || "").toLowerCase(); });
    if (row.some(function(c) { return c.includes("future owner") || c.includes("level of trust"); })) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) {
    // Return raw fallback
    return { rows: [], rawValues: values, parseError: "Could not identify header row" };
  }

  const headers = values[headerIdx].map(function(c) { return String(c || "").trim().toLowerCase(); });

  // Column index finders
  function col(keyword) {
    return headers.findIndex(function(h) { return h.includes(keyword); });
  }

  const colSystem = 1; // System name is typically col B (index 1) in merged structure
  const colDept = col("department");
  const colOwner = col("current owner");
  const colFuture = col("future owner");
  const colTrust = col("trust");
  const colStory = col("connected story");
  const colEnd = col("end user");

  const rows = [];
  let currentCategory = "";

  for (let i = headerIdx + 1; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c; })) continue;

    const system = String(row[colSystem] || row[0] || "").trim();
    if (!system) continue;

    // Detect category headers (Bullhorn, Excel, PowerPoint, PowerBI, etc.)
    const isCategoryHeader = row.slice(2).every(function(c) { return !c; }) &&
      (system.includes("Bullhorn") || system.includes("Excel") ||
       system.includes("PowerPoint") || system.includes("PowerBI") ||
       system.includes("Smart Sheets") || system.includes("Power Automate"));

    if (isCategoryHeader) {
      currentCategory = system;
      continue;
    }

    // Skip asterisk notes
    if (system.startsWith("*")) continue;

    const trust = String(colTrust >= 0 ? (row[colTrust] || "") : "").trim().toLowerCase();
    const owner = String(colOwner >= 0 ? (row[colOwner] || "") : "").trim();
    const future = String(colFuture >= 0 ? (row[colFuture] || "") : "").trim();
    const story = String(colStory >= 0 ? (row[colStory] || "") : "").trim();
    const dept = String(colDept >= 0 ? (row[colDept] || "") : "").trim();

    // Determine action from context
    let action = "evaluate";
    const systemLower = system.toLowerCase();
    if (systemLower.includes("netsuite") || systemLower.includes("paycom") ||
        systemLower.includes("linkedin") || systemLower.includes("zoom") ||
        systemLower.includes("refassure") || systemLower.includes("great recruiters") ||
        systemLower.includes("intelagree") || systemLower.includes("indeed")) {
      action = "keep";
    } else if (systemLower.includes("bullhorn") || systemLower.includes("leadr")) {
      action = "fix";
    } else if (systemLower.includes("chatgpt") || systemLower.includes("cdata") ||
               systemLower.includes("monthly ops") || systemLower.includes("burndown") ||
               systemLower.includes("westview")) {
      action = "replace";
    } else if (systemLower.includes("fp&a") || systemLower.includes("upt") ||
               systemLower.includes("master billing") || systemLower.includes("weekly billing") ||
               systemLower.includes("roadmap")) {
      action = "migrate";
    }

    // Determine priority
    let priority = "P2";
    if (action === "fix" || systemLower.includes("netsuite") ||
        systemLower.includes("monthly ops") || systemLower.includes("master billing") ||
        systemLower.includes("weekly billing")) {
      priority = "P0";
    } else if (action === "replace" || action === "migrate" ||
               systemLower.includes("paycom") || systemLower.includes("linkedin") ||
               systemLower.includes("zoominfo") || systemLower.includes("vms sync")) {
      priority = "P1";
    } else if (systemLower.includes("mercury") || systemLower.includes("vaas") ||
               systemLower.includes("leadr")) {
      priority = "P3";
    }

    rows.push({
      system: currentCategory ? currentCategory + " " + system : system,
      category: currentCategory || "Other",
      dept: dept,
      owner: owner,
      futureOwner: future,
      trust: trust || "n/a",
      stories: story,
      action: action,
      priority: priority,
    });
  }

  return { rows, parseError: rows.length === 0 ? "No rows parsed" : null };
}

export async function fetchDataAudit(accessToken) {
  try {
    const driveItem = await resolveSharingUrl(SHARING_URLS.dataAudit, accessToken);
    const range = await getWorksheetRange(driveItem, "Sheet1", accessToken);
    const { rows, rawValues, parseError } = parseDataAuditSheet(range.values);
    return { rows, rawValues: rawValues || range.values, parseError, lastUpdated: new Date().toISOString() };
  } catch (err) {
    throw new Error("Data Audit fetch failed: " + err.message);
  }
}

// ─── ONBOARDING TRACKER ───────────────────────────────────────────────────────

// Parse a single onboarding week sheet into rows
function parseOnboardingSheet(values, sheetName) {
  if (!values || values.length < 2) return [];

  // Find header row - look for "Start Date" or "Candidate"
  let headerIdx = -1;
  for (let i = 0; i < Math.min(values.length, 5); i++) {
    const row = values[i].map(function(c) { return String(c || "").toLowerCase(); });
    if (row.some(function(c) { return c.includes("start date") || c === "candidate"; })) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) return [];

  const headers = values[headerIdx].map(function(c) { return String(c || "").trim().toLowerCase(); });

  function col(keyword) {
    return headers.findIndex(function(h) { return h.includes(keyword); });
  }

  const colAssignment = col("assignment");
  const colType = col("type");
  const colPct = headers.findIndex(function(h) { return h === "%" || h === "percent"; });
  const colStatus = col("completion status");
  const colCandidate = col("candidate");
  const colSalesRep = col("sales rep");
  const colRecruiter = col("recruiter");
  const colClient = col("client name");
  const colHire = col("new hire or rehire");
  const colBH = col("bh status");
  const colNotes = col("notes");
  const colBgCheck = col("background check");

  const rows = [];

  for (let i = headerIdx + 1; i < values.length; i++) {
    const row = values[i];
    if (!row || row.every(function(c) { return !c || c === 1; })) continue;

    const candidate = String(colCandidate >= 0 ? (row[colCandidate] || "") : "").trim();
    if (!candidate) continue;

    // Parse completion status color
    const statusRaw = String(colStatus >= 0 ? (row[colStatus] || "") : "").trim();
    const status = ["Green", "Yellow", "Orange", "Gray"].includes(statusRaw) ? statusRaw : "Gray";

    // Parse percentage
    let pct = 0;
    if (colPct >= 0 && row[colPct] !== undefined && row[colPct] !== "") {
      const pctVal = parseFloat(row[colPct]);
      pct = isNaN(pctVal) ? 0 : pctVal;
    }

    // Parse start date (Excel serial number)
    let startDate = "";
    const startSerial = row[0];
    if (startSerial && typeof startSerial === "number" && startSerial > 40000) {
      const d = new Date((startSerial - 25569) * 86400 * 1000);
      startDate = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } else if (startSerial) {
      startDate = String(startSerial);
    }

    const bhStatus = String(colBH >= 0 ? (row[colBH] || "") : "").trim();
    const isNonStarter = bhStatus === "NonStarter" || status === "Gray" ||
      String(colType >= 0 ? row[colType] : "").includes("NonStarter");

    rows.push({
      assignment: String(colAssignment >= 0 ? (row[colAssignment] || "") : "").trim(),
      type: isNonStarter ? "NonStarter" : String(colType >= 0 ? (row[colType] || "W2") : "W2").trim(),
      pct: isNonStarter ? 0 : pct,
      status: isNonStarter ? "Gray" : status,
      candidate: candidate,
      salesRep: String(colSalesRep >= 0 ? (row[colSalesRep] || "") : "").trim(),
      recruiter: String(colRecruiter >= 0 ? (row[colRecruiter] || "") : "").trim(),
      client: String(colClient >= 0 ? (row[colClient] || "") : "").trim(),
      hire: String(colHire >= 0 ? (row[colHire] || "") : "").trim(),
      bhStatus: bhStatus || (isNonStarter ? "NonStarter" : ""),
      notes: String(colNotes >= 0 ? (row[colNotes] || "") : "").trim(),
      bgCheck: String(colBgCheck >= 0 ? (row[colBgCheck] || "") : "").trim(),
      startDate: startDate,
      week: sheetName,
    });
  }

  return rows;
}

export async function fetchOnboardingData(accessToken) {
  try {
    const driveItem = await resolveSharingUrl(SHARING_URLS.onboarding, accessToken);
    const worksheets = await getWorksheets(driveItem, accessToken);

    // Sort sheets by name (date format MM.DD.YY)
    const sorted = worksheets
      .map(function(ws) { return ws.name; })
      .filter(function(name) { return /^\d{2}\.\d{2}\.\d{2}$/.test(name) || /^WE \d{2}\.\d{2}\.\d{2}$/.test(name); })
      .sort(function(a, b) {
        // Parse dates for comparison
        const parseDate = function(s) {
          const clean = s.replace("WE ", "");
          const parts = clean.split(".");
          if (parts.length !== 3) return 0;
          const month = parseInt(parts[0]);
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]) + (parseInt(parts[2]) < 50 ? 2000 : 1900);
          return new Date(year, month - 1, day).getTime();
        };
        return parseDate(b) - parseDate(a); // descending - most recent first
      });

    // Fetch the most recent 10 sheets to keep it fast
    const sheetsToFetch = sorted.slice(0, 10);
    const weekData = {};

    await Promise.all(sheetsToFetch.map(async function(sheetName) {
      try {
        const range = await getWorksheetRange(driveItem, sheetName, accessToken);
        weekData[sheetName] = parseOnboardingSheet(range.values, sheetName);
      } catch (e) {
        weekData[sheetName] = [];
      }
    }));

    return {
      weeks: sheetsToFetch,
      allWeeks: sorted,
      weekData,
      lastUpdated: new Date().toISOString(),
    };
  } catch (err) {
    throw new Error("Onboarding fetch failed: " + err.message);
  }
}

// Fetch a specific week on demand (for lazy loading older weeks)
export async function fetchOnboardingWeek(accessToken, sheetName) {
  try {
    const driveItem = await resolveSharingUrl(SHARING_URLS.onboarding, accessToken);
    const range = await getWorksheetRange(driveItem, sheetName, accessToken);
    return parseOnboardingSheet(range.values, sheetName);
  } catch (err) {
    throw new Error("Week fetch failed: " + err.message);
  }
}

// Fetches the live org directory from Graph, filtered to real TCC staff
// (excludes disabled accounts and external/guest accounts) and mapped to
// a flat shape every consumer (owner dropdowns, resolveOwnerEmails,
// notify-server sync) can use directly. Org-wide call, not scoped to a
// specific group -- TCC is ~15 people, no group-scoping needed.
export async function fetchOrgUsers(accessToken) {
  let url = GRAPH_BASE + "/users?$select=id,displayName,mail,businessPhones,mobilePhone,jobTitle,userType,accountEnabled&$top=999";
  let users = [];
  while (url) {
    const data = await graphFetch(url, accessToken);
    users = users.concat(data.value || []);
    url = data["@odata.nextLink"] || null;
  }
  return users
    .filter(function (u) { return u.accountEnabled === true && u.userType !== "Guest"; })
    .map(function (u) {
      return {
        id: u.id,
        displayName: u.displayName || "",
        mail: u.mail || "",
        phone: u.mobilePhone || (u.businessPhones && u.businessPhones[0]) || "",
        jobTitle: u.jobTitle || "",
      };
    });
}
